import { useEffect, useSyncExternalStore } from 'react'
import { useAuthContext } from '../../context/auth.context'
import { lmsAdmin } from './api'

// Whether the LMS admin is staff-only on this environment. The server decides
// (the LMS service's LMS_STAFF_ONLY setting, answered by GET /admin/lms/access);
// the admin is built once for every environment and holds no switch of its
// own. The server still enforces on every route: this only decides what is
// drawn.
//
// It fails closed, and nothing waits for it. Until the server has answered,
// when it cannot be read (a short timeout, api.js), or when it says anything
// but `staffOnly: false`, the LMS admin is staff-only: pages draw as they do
// where the LMS stays closed, and the LMS is added only if the answer arrives
// and opens it.
//
// It is asked once the session's token has been refreshed (the claims are
// known): the stored token of a reloaded tab may have expired, and a 401 then
// would leave a mounted menu closed. A failed read is asked again only when
// the token changes (a refresh), never on a mere mount, so an older LMS that
// answers 404 is not asked on every page. The answer is the environment's,
// not a person's: shared by every caller (the sidebar's two copies, the LMS
// pages, the analytics tab) and asked once per page for whoever signs in.

// How long a failed read waits before it is tried once more (a hiccup, a slow
// pod); after that only a new token asks again.
export const RETRY_AFTER_MS = 3000

const CLOSED = 'closed'
const OPEN = 'open'
const UNKNOWN = 'unknown'

let state = UNKNOWN
let asking = false
let failed = false // the last read failed (an answer, even a closed one, is final)
let askedWith = null // the token of the last read (in flight or failed)
let wantedWith = null // the newest token a caller has offered
const retriedFor = new Set() // tokens whose failure has had its one delayed retry
let generation = 0
const listeners = new Set()

const notify = () => listeners.forEach(listener => listener())

const subscribe = listener => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

// Tests start each case from a fresh page.
export const forgetLmsAccess = () => {
  generation += 1
  state = UNKNOWN
  asking = false
  failed = false
  askedWith = null
  wantedWith = null
  retriedFor.clear()
}

const read = token => {
  wantedWith = token
  if (asking || state === OPEN || (state === CLOSED && (!failed || askedWith === token))) return
  const mine = generation
  asking = true
  askedWith = token
  const done = (next, didFail) => {
    if (mine !== generation) return
    asking = false
    failed = didFail
    state = next
    notify()
    // The token changed while this read was in flight: ask with the new one.
    if (didFail && wantedWith !== askedWith) {
      read(wantedWith)
    } else if (didFail && !retriedFor.has(askedWith)) {
      // The same token failed: not a stale token, so likely a hiccup. Once.
      const failedWith = askedWith
      retriedFor.add(failedWith)
      setTimeout(() => {
        if (mine === generation && failed && !asking && state === CLOSED) {
          askedWith = null
          read(wantedWith || failedWith)
        }
      }, RETRY_AFTER_MS)
    }
  }
  Promise.resolve()
    .then(() => lmsAdmin.getAccess())
    .then(
      answer => done(answer && answer.staffOnly === false ? OPEN : CLOSED, false),
      () => done(CLOSED, true)
    )
}

// Whether the LMS admin is staff-only right now (true until the server says
// otherwise). Read at call time.
export const getLmsStaffOnly = () => state !== OPEN

// Asks the server once the person is signed in, and re-renders the caller
// when the answer arrives. Call it in any component that reads the two
// functions above.
export const useLmsAccess = () => {
  const auth = useAuthContext()
  const authState = auth && auth.state
  const token = authState && authState.accessToken
  // After the refresh: the claims arrive with it (the same signal as the
  // gate's claimsKnown, which this module cannot import).
  const claims = authState && authState.jwtClaims
  const ready = !!token && !!claims && Object.keys(claims).length > 0

  useEffect(() => {
    if (ready) read(token)
  }, [ready, token])

  return useSyncExternalStore(subscribe, () => state)
}
